// Shopify Forms owns uploads, validation and submission. This adapter only
// removes a redundant email field, adds story guidance and reveals extra photos.
if (!customElements.get('comic-story-form')) {
  customElements.define('comic-story-form', class extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.roots = new Map();
      this.observer = new MutationObserver(() => this.findForms());
      this.observer.observe(this, { childList: true, subtree: true });
      const lifecycle = this.lifecycle;
      customElements.whenDefined('shopify-forms-embed').then(() => {
        if (this.lifecycle === lifecycle && this.isConnected) this.findForms();
      });
      this.findForms();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = null;
      this.observer?.disconnect();
      this.roots?.forEach(state => this.releaseForm(state));
      this.roots?.clear();
    }

    findForms() {
      if (!this.lifecycle || !this.isConnected) return;
      for (const [root, state] of this.roots) {
        if (!this.contains(root.host)) {
          this.releaseForm(state);
          this.roots.delete(root);
        }
      }
      for (const host of this.querySelectorAll('.comic-community__app-form shopify-forms-embed')) {
        const root = host.shadowRoot;
        if (!root || this.roots.has(root)) continue;
        const state = { root, lifecycle: new AbortController(), observer: new MutationObserver(() => this.updateForm(state)) };
        this.roots.set(root, state);
        state.observer.observe(root, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-invalid', 'value', 'disabled', 'id', 'for', 'type'] });
        const options = { capture: true, signal: state.lifecycle.signal };
        root.addEventListener('input', () => this.updateForm(state), options);
        root.addEventListener('change', () => this.updateForm(state), options);
        root.addEventListener('invalid', () => this.updateForm(state), options);
        root.addEventListener('focusout', () => queueMicrotask(() => this.updateForm(state)), options);
        root.addEventListener('submit', event => {
          this.updateForm(state);
          if (state.story?.value.length > this.storyLimit) {
            event.preventDefault();
            event.stopImmediatePropagation();
            state.story.focus();
          }
        }, options);
        root.addEventListener('reset', () => queueMicrotask(() => {
          state.revealedPhotos?.clear();
          if (this.lifecycle && this.isConnected) this.updateForm(state);
        }), options);
        this.updateForm(state);
      }
      this.updateEmailFallback();
    }

    get storyLimit() {
      return 20000;
    }

    updateForm(state) {
      if (!this.lifecycle || state.lifecycle.signal.aborted || !this.isConnected || !this.contains(state.root.host)) return;
      const story = state.root.querySelector('textarea[name="custom#pets_story"]');
      if (!story) {
        this.releaseGuidance(state);
        this.releasePhotos(state);
        this.showEmail(state);
        state.emailFound = false;
        this.updateEmailFallback();
        return;
      }

      if (!state.style?.isConnected) {
        state.style = document.createElement('style');
        state.style.textContent = `
          [data-comic-account-email] { display: none !important; }
          [data-comic-photo-collapsed], [data-comic-photo-actions][hidden] { display: none !important; }
          [data-comic-add-photo] { display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 48px; padding: 12px 16px; border: 1px dashed var(--community-ink, var(--color-text)); border-radius: 999px; background: transparent; color: var(--community-ink, var(--color-text)); font: inherit; font-family: var(--font-body--family, var(--forms-body-font)); font-size: var(--text-sm, 15px); font-weight: 500; line-height: 1.5; cursor: pointer; }
          [data-comic-add-photo]:hover { background: var(--color-care-soft, var(--color-background)); }
          [data-comic-add-photo]:focus-visible { outline: 2px solid var(--community-ink, var(--color-text)); outline-offset: 4px; }
          [data-comic-add-photo]:disabled { cursor: wait; opacity: .65; }
          [data-comic-photo-plus] { font-size: 24px; line-height: 1; font-weight: 400; }
          [data-comic-story-guidance] { margin: 0; color: var(--community-muted, var(--color-text)); font: var(--forms-body-font); font-size: var(--text-xs, 13px); line-height: 1.5; }
          [data-comic-story-summary] { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 4px 16px; }
          [data-comic-story-count] { white-space: nowrap; }
          [data-comic-story-error] { display: block; color: var(--community-ink, var(--color-text)); margin-top: 4px; }
          [data-comic-story-error][hidden] { display: none !important; }
        `;
        state.root.appendChild(state.style);
      }
      if (state.story !== story || !state.guidance?.isConnected) {
        this.releaseGuidance(state);
        state.story = story;
        const guidance = document.createElement('p');
        guidance.dataset.comicStoryGuidance = '';
        guidance.id = `${this.id || 'ComicAppForm'}-story-help`;
        const summary = document.createElement('span');
        summary.dataset.comicStorySummary = '';
        const help = document.createElement('span');
        help.textContent = this.dataset.storyHelp;
        const count = document.createElement('span');
        count.dataset.comicStoryCount = '';
        count.setAttribute('aria-hidden', 'true');
        const error = document.createElement('span');
        error.dataset.comicStoryError = '';
        error.setAttribute('role', 'status');
        error.hidden = true;
        summary.append(help, count);
        guidance.append(summary, error);
        story.parentElement.after(guidance);
        state.guidance = guidance;
        state.count = count;
        state.error = error;
      }
      const descriptions = (story.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (!descriptions.includes(state.guidance.id)) {
        story.setAttribute('aria-describedby', [...descriptions, state.guidance.id].join(' '));
      }
      // Match Forms' UTF-16 string length. Keep over-limit drafts intact so the
      // customer can edit them; Forms retains its own 20,000-character validator.
      const countText = `${story.value.length} / ${this.storyLimit}`;
      if (state.count.textContent !== countText) state.count.textContent = countText;
      const overLimit = story.value.length > this.storyLimit;
      const errorText = overLimit ? this.dataset.storyError : '';
      if (state.error.textContent !== errorText) state.error.textContent = errorText;
      state.error.hidden = !overLimit;
      this.updatePhotos(state);

      const email = state.root.querySelector('input#email[data-testid="field-email"]');
      const group = email?.parentElement;
      const accountEmail = this.dataset.customerEmail?.trim();
      const validation = document.createElement('input');
      validation.type = 'email';
      validation.value = accountEmail || '';
      const hasError = email?.getAttribute('aria-invalid') === 'true' ||
        (group?.nextElementSibling?.matches('output[role="alert"]') && !!group.nextElementSibling.textContent.trim());
      const safeGroup = group?.matches('div') && group.querySelector('label[for="email"]') && group.querySelectorAll('input, select, textarea').length === 1;
      const isAccountEmail = !!accountEmail && validation.validity.valid && /@[^\s@]+\.[^\s@]+$/.test(accountEmail) && email?.value === accountEmail;
      state.emailFound = !!email;
      if (safeGroup && isAccountEmail && !hasError && state.root.activeElement !== email) {
        if (state.emailGroup !== group) this.showEmail(state);
        group.setAttribute('data-comic-account-email', '');
        state.emailGroup = group;
      } else {
        this.showEmail(state);
      }
      this.updateEmailFallback();
    }

    updatePhotos(state) {
      const inputs = [...state.root.querySelectorAll('input[type="file"][id^="custom#pets_photo"]')];
      const photos = inputs.map(input => {
        const label = input.parentElement;
        const dropzone = label?.parentElement;
        const group = dropzone?.parentElement;
        const title = group?.querySelector(':scope > p')?.textContent.trim();
        const number = /^Pet[’']s photo$/.test(title || '') ? 1 : Number(title?.match(/^Pet[’']s photo ([2-5]) \(optional\)$/)?.[1]);
        if (!number || !label?.matches('label') || label.htmlFor !== input.id || !dropzone?.matches('div') || !group?.matches('div') || group.querySelectorAll('input, select, textarea').length !== 1) return null;
        return { input, group, number };
      });
      // Only collapse a fully recognized native Forms layout. Leave unfamiliar
      // fields visible if the app changes its markup or the merchant edits labels.
      const form = photos[0]?.input.closest('form');
      if (!form || photos.length !== 5 || photos.some(photo => !photo || photo.group.parentElement !== photos[0].group.parentElement || photo.input.closest('form') !== form) || new Set(photos.map(photo => photo.number)).size !== 5 || !this.dataset.addPhoto) {
        this.releasePhotos(state);
        return;
      }
      photos.sort((a, b) => a.number - b.number);
      if (state.photoForm !== form) {
        this.releasePhotos(state);
        state.photoForm = form;
        state.revealedPhotos = new Set();
      }
      const optional = photos.slice(1);
      const currentGroups = new Set(optional.map(photo => photo.group));
      state.photoGroups?.forEach(group => {
        if (!currentGroups.has(group)) group.removeAttribute('data-comic-photo-collapsed');
      });
      state.photoGroups = currentGroups;
      for (const { input, group } of optional) {
        const selected = [...group.querySelectorAll('button')].some(button => button.id === input.id.replace(/-field$/, '-clear'));
        const error = input.getAttribute('aria-invalid') === 'true' || [...group.querySelectorAll('output[role="alert"]')].some(output => output.textContent.trim());
        if (selected || input.files?.length || input.value || input.disabled || error || group.contains(state.root.activeElement)) state.revealedPhotos.add(input.id);
        group.toggleAttribute('data-comic-photo-collapsed', !state.revealedPhotos.has(input.id));
      }
      if (!state.photoActions?.isConnected) {
        const actions = document.createElement('div');
        actions.dataset.comicPhotoActions = '';
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.comicAddPhoto = '';
        const plus = document.createElement('span');
        plus.dataset.comicPhotoPlus = '';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        const text = document.createElement('span');
        text.textContent = this.dataset.addPhoto;
        button.append(plus, text);
        button.addEventListener('click', () => {
          this.updatePhotos(state);
          if (button.disabled) return;
          const next = state.photos?.find(photo => photo.group.hasAttribute('data-comic-photo-collapsed'));
          if (!next) return;
          state.revealedPhotos.add(next.input.id);
          this.updatePhotos(state);
          next.input.focus();
        }, { signal: state.lifecycle.signal });
        actions.append(button);
        state.photoActions = actions;
        state.addPhotoButton = button;
      }
      state.photos = optional;
      const next = optional.find(photo => photo.group.hasAttribute('data-comic-photo-collapsed'));
      const anchor = next?.group || optional.at(-1).group;
      if (next && state.photoActions.nextElementSibling !== anchor) anchor.before(state.photoActions);
      else if (!state.photoActions.isConnected) anchor.after(state.photoActions);
      state.photoActions.hidden = !next;
      const busy = !!form.querySelector('button[type="submit"]:disabled');
      if (state.addPhotoButton.disabled !== busy) state.addPhotoButton.disabled = busy;
    }

    releasePhotos(state) {
      state.photoGroups?.forEach(group => group.removeAttribute('data-comic-photo-collapsed'));
      state.photoActions?.remove();
      state.photoActions = null;
      state.photoGroups = null;
      state.photoForm = null;
      state.photos = null;
      state.revealedPhotos = null;
    }

    showEmail(state) {
      state.emailGroup?.removeAttribute('data-comic-account-email');
      state.emailGroup = null;
    }

    updateEmailFallback() {
      const fallback = this.querySelector('[data-account-email-fallback]');
      if (fallback) fallback.hidden = ![...this.roots.values()].some(state => state.emailFound && !state.emailGroup);
    }

    releaseGuidance(state) {
      if (state.story && state.guidance) {
        const descriptions = (state.story.getAttribute('aria-describedby') || '').split(/\s+/).filter(value => value && value !== state.guidance.id);
        if (descriptions.length) state.story.setAttribute('aria-describedby', descriptions.join(' '));
        else state.story.removeAttribute('aria-describedby');
      }
      state.guidance?.remove();
      state.story = null;
      state.guidance = null;
    }

    releaseForm(state) {
      state.lifecycle.abort();
      state.observer.disconnect();
      this.releaseGuidance(state);
      this.releasePhotos(state);
      this.showEmail(state);
      state.style?.remove();
    }
  });
}

if (!customElements.get('comic-community')) {
  customElements.define('comic-community', class extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.form = this.querySelector('[data-story-form]');
      this.fields = this.querySelector('[data-form-fields]');
      this.photo = this.querySelector('[data-photo-input]');
      this.story = this.querySelector('[name="pet_story"]');
      this.submit = this.querySelector('[data-submit]');
      this.status = this.querySelector('[data-form-status]');
      if (!this.form || !this.fields || !this.photo || !this.story || !this.submit || !this.status) return;

      const preview = this.querySelector('[data-photo-preview]');
      this.preview = preview?.matches('img') ? preview : preview?.querySelector('img');
      this.filename = this.querySelector('[data-photo-filename]');
      this.empty = this.querySelector('[data-photo-empty]');
      this.remove = this.querySelector('[data-remove-photo]');
      this.photoError = this.querySelector('[data-photo-error]');
      this.count = this.querySelector('[data-story-count]');
      this.success = this.querySelector('[data-submission-success]');
      this.signInAgain = this.querySelector('[data-sign-in-again]');
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };

      this.photo.addEventListener('change', () => this.updatePhoto(), options);
      this.remove?.addEventListener('click', event => {
        event.preventDefault();
        this.photo.value = '';
        this.updatePhoto();
        this.photo.focus();
      }, options);
      this.form.addEventListener('input', () => {
        this.updateCount();
        if (this.success) this.success.hidden = true;
      }, options);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.submitStory();
      }, options);
      this.form.addEventListener('reset', () => {
        const lifecycle = this.lifecycle;
        queueMicrotask(() => {
          if (this.lifecycle !== lifecycle || !this.isConnected) return;
          this.updatePhoto();
          this.updateCount();
        });
      }, options);

      this.setBusy(false);
      this.updatePhoto();
      this.updateCount();
      this.status.textContent = this.sessionExpired ? this.dataset.sessionExpired : (this.endpoint ? (this.interrupted ? this.dataset.error : '') : this.dataset.unavailable);
      if (this.signInAgain) this.signInAgain.hidden = !this.sessionExpired;
      this.interrupted = false;
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = null;
      if (this.request) {
        this.interrupted = true;
        clearTimeout(this.request.timer);
        this.request.controller.abort();
        this.request = null;
      }
      this.releasePreview();
      if (this.fields) this.fields.disabled = true;
      if (this.submit) this.submit.disabled = true;
      this.form?.removeAttribute('aria-busy');
    }

    get endpoint() {
      const value = this.dataset.submissionUrl?.trim();
      if (!value) return null;
      try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin || !url.pathname.startsWith('/apps/') || url.username || url.password) return null;
        url.hash = '';
        return url.href;
      } catch {
        return null;
      }
    }

    fileError(file) {
      if (!file) return '';
      if (file.size <= 0 || file.size > 20 * 1024 * 1024) return this.dataset.fileSizeError;
      const type = file.type.toLowerCase();
      const knownType = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(type);
      const knownExtension = /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
      return knownType || ((!type || type === 'application/octet-stream') && knownExtension) ? '' : this.dataset.fileTypeError;
    }

    updatePhoto() {
      this.releasePreview();
      const file = this.photo.files?.[0];
      const error = this.fileError(file);
      this.photo.setCustomValidity(error || '');
      this.showPhotoError(error, !!error);
      const selected = !!file && !error;
      if (this.filename) {
        this.filename.textContent = selected ? [file.name, this.dataset.photoSelected].filter(Boolean).join(' · ') : '';
        this.filename.hidden = !selected;
      }
      if (this.empty) this.empty.hidden = selected;
      if (this.remove) this.remove.hidden = !file;
      if (!selected || !this.preview) return;

      try {
        const url = URL.createObjectURL(file);
        this.previewUrl = url;
        this.preview.onload = () => {
          if (this.isConnected && this.previewUrl === url) this.preview.hidden = false;
        };
        this.preview.onerror = () => {
          if (!this.isConnected || this.previewUrl !== url) return;
          this.releasePreview();
          this.showPhotoError(this.dataset.filePreviewError);
        };
        this.preview.src = url;
      } catch {
        this.showPhotoError(this.dataset.filePreviewError);
      }
    }

    releasePreview() {
      if (this.preview) {
        this.preview.onload = null;
        this.preview.onerror = null;
        this.preview.hidden = true;
        this.preview.removeAttribute('src');
      }
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }

    showPhotoError(message, invalid = false) {
      if (this.photoError) {
        this.photoError.textContent = message || '';
        this.photoError.hidden = !message;
      }
      if (invalid) this.photo.setAttribute('aria-invalid', 'true');
      else this.photo.removeAttribute('aria-invalid');
    }

    updateCount() {
      if (this.count) this.count.textContent = `${this.story.value.length} / ${this.story.maxLength > 0 ? this.story.maxLength : 20000}`;
    }

    setBusy(busy) {
      this.fields.disabled = busy;
      this.submit.disabled = busy || !this.endpoint || !!this.sessionExpired;
      this.form.setAttribute('aria-busy', String(busy));
    }

    async submitStory() {
      if (this.request || !this.lifecycle || !this.isConnected || this.sessionExpired) return;
      const endpoint = this.endpoint;
      if (!endpoint) {
        this.submit.disabled = true;
        this.status.textContent = this.dataset.unavailable;
        return;
      }
      const error = this.fileError(this.photo.files?.[0]);
      this.photo.setCustomValidity(error || '');
      if (error) this.showPhotoError(error, true);
      if (!this.form.reportValidity()) return;

      const body = new FormData(this.form);
      const request = { controller: new AbortController(), timer: null };
      this.request = request;
      this.setBusy(true);
      this.status.textContent = this.dataset.pending;
      if (this.success) this.success.hidden = true;
      request.timer = setTimeout(() => {
        if (this.request !== request) return;
        this.request = null;
        request.controller.abort();
        this.setBusy(false);
        this.status.textContent = this.dataset.error;
      }, 30000);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body,
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          mode: 'same-origin',
          redirect: 'error',
          signal: request.controller.signal
        });
        if (response.status === 401) {
          if (this.request !== request || !this.isConnected) return;
          this.sessionExpired = true;
          this.status.textContent = this.dataset.sessionExpired;
          if (this.signInAgain) this.signInAgain.hidden = false;
          return;
        }
        if (!response.ok) throw new Error('Submission was not accepted.');
        const result = await response.json();
        if (result?.ok !== true || typeof result.id !== 'string' || !result.id.trim()) throw new Error('Submission was not confirmed.');
        if (this.request !== request || !this.isConnected) return;
        this.form.reset();
        this.status.textContent = this.dataset.success;
        if (this.success) this.success.hidden = false;
      } catch {
        if (this.request !== request || !this.isConnected) return;
        this.status.textContent = this.dataset.error;
      } finally {
        clearTimeout(request.timer);
        if (this.request === request) {
          this.request = null;
          this.setBusy(false);
        }
      }
    }
  });
}
